/**
 * AdvancedVoicePanel - Premium voice testing panel
 * Orb as background, chat bubbles overlay the orb area
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, PhoneOff, Mic, MicOff, Loader2, 
  AlertCircle, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import VoiceOrb from '../widgets/BlackHoleOrb';

// Load the EL ConvAI SDK via the IIFE bundle (same as prod orb widget).
// The ESM build has a known fetch-body-double-read bug when proxied
// through CloudFront/Ingress (see error: "Failed to execute 'text' on
// 'Response': body stream already read"). The IIFE bundle ships with a
// forked fetch wrapper that handles error bodies correctly.
const EL_SDK_URL = 'https://cdn.jsdelivr.net/npm/@elevenlabs/client@1.4.0/dist/lib.iife.js';
let _elSdkPromise = null;
function loadElevenLabsSdk() {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  if (window.ElevenLabsClient) return Promise.resolve(window.ElevenLabsClient);
  if (_elSdkPromise) return _elSdkPromise;
  _elSdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${EL_SDK_URL}"]`);
    const onLoad = () => {
      if (window.ElevenLabsClient) resolve(window.ElevenLabsClient);
      else reject(new Error('ElevenLabs SDK failed to attach to window'));
    };
    if (existing) {
      existing.addEventListener('load', onLoad, { once: true });
      existing.addEventListener('error', () => reject(new Error('ElevenLabs SDK failed to load')), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = EL_SDK_URL;
    s.async = true;
    s.onload = onLoad;
    s.onerror = () => reject(new Error('ElevenLabs SDK failed to load'));
    document.head.appendChild(s);
  });
  return _elSdkPromise;
}

// Error boundary wrapper
class VoicePanelErrorBoundary extends React.Component {
  state = { hasError: false, errorMsg: '' };
  static getDerivedStateFromError(error) { return { hasError: true, errorMsg: error?.message || String(error) }; }
  componentDidCatch(error, info) {
    console.error('[VoicePanel] Error boundary caught:', error, info?.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-full p-4 text-center">
          <div>
            <AlertCircle className="mx-auto mb-2 text-red-400" size={24} />
            <p className="text-sm text-red-400">Voice panel encountered an error</p>
            <p className="text-xs text-gray-500 mt-1 max-w-xs">{this.state.errorMsg}</p>
            <button onClick={() => this.setState({ hasError: false, errorMsg: '' })} className="mt-2 text-xs text-cyan-400 hover:underline">Retry</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const AdvancedVoicePanelInner = ({ agentId, isDarkMode, api }) => {
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  
  // Connection state
  const [connectionState, setConnectionState] = useState('disconnected');
  const conversationRef = useRef(null);
  const isActiveRef = useRef(false);
  // Test-orb voice session tracking — backend bills the workspace for the
  // minutes used. We capture the session_id on /test-orb/session, the start ts on
  // first connect, and POST .../end on every disconnect path (graceful,
  // error, unmount, beforeunload).
  const sessionIdRef = useRef(null);
  const providerConversationRef = useRef(null);
  const sessionAgentIdRef = useRef(null);
  const sessionStartTsRef = useRef(null);
  const sessionEndedRef = useRef(false);
  const agentSpokeRef = useRef(false);
  
  // Audio state
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [isAgentThinking, setIsAgentThinking] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const audioLevelIntervalRef = useRef(null);
  
  // UI state
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState(null);
  const [activeFunctionCall, setActiveFunctionCall] = useState(null);
  
  // Conversation
  const [conversationHistory, setConversationHistory] = useState([]);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const messagesEndRef = useRef(null);

  // Balance-exhausted modal state. When `/api/voice/token` returns 402
  // we surface a friendly "top up" modal instead of a scary error toast.
  // The billing redirect is rebilling-aware: agency→workspace billing if
  // rebilling is OFF, sub-account→sub-account billing when rebilling is ON.
  const [balanceGate, setBalanceGate] = useState(null); // {workspaceId, subAccountId, balance, rebilling}

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationHistory, currentTranscript]);

  // Audio level polling — ElevenLabs exposes `getOutputVolume()` on the
  // Conversation instance (WebRTC + WebSocket), so we sample it while
  // connected to drive the orb's visual pulse. Same 50ms cadence as
  // before so the UI shimmer stays smooth.
  useEffect(() => {
    if (connectionState === 'connected' && conversationRef.current) {
      audioLevelIntervalRef.current = setInterval(() => {
        try {
          const conv = conversationRef.current;
          if (!conv) return;
          let level = 0;
          if (typeof conv.getOutputVolume === 'function') {
            level = conv.getOutputVolume() || 0;
          }
          setAudioLevel(Math.min(1, level));
        } catch (_) { /* ignore */ }
      }, 50);
    }
    return () => {
      if (audioLevelIntervalRef.current) {
        clearInterval(audioLevelIntervalRef.current);
        audioLevelIntervalRef.current = null;
      }
    };
  }, [connectionState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (conversationRef.current) {
        try { conversationRef.current.endSession(); } catch (_) {}
        conversationRef.current = null;
      }
      isActiveRef.current = false;
      // Bill any session that's still open when the panel unmounts.
      reportSessionEnd();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire end-event on tab close too — protects against the user closing
  // the browser without clicking hangup. sendBeacon survives unload.
  useEffect(() => {
    const onPageHide = () => reportSessionEnd();
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onPageHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reportSessionEnd = () => {
    const sid = sessionIdRef.current;
    const aid = sessionAgentIdRef.current;
    if (!sid || !aid || sessionEndedRef.current) return;
    sessionEndedRef.current = true;
    const startTs = sessionStartTsRef.current;
    const duration_seconds = startTs ? Math.max(0, Math.floor((Date.now() - startTs) / 1000)) : 0;
    sessionIdRef.current = null;
    sessionAgentIdRef.current = null;
    sessionStartTsRef.current = null;
    const endUrl = `/api/agents/${encodeURIComponent(aid)}/test-orb/session/${encodeURIComponent(sid)}/end`;
    const endPayload = { duration_seconds, conversation_id: providerConversationRef.current || conversationRef.current?.getId?.() || null };
    providerConversationRef.current = null;
    try {
      if (document.visibilityState === 'hidden' && navigator.sendBeacon) {
        // Unload-path — auth header can't be attached to sendBeacon, but
        // the server-side stale-session sweeper is the safety net.
        const blob = new Blob([JSON.stringify(endPayload)], { type: 'application/json' });
        navigator.sendBeacon(endUrl, blob);
      } else if (api && api.post) {
        api.post(endUrl, endPayload).catch(() => {});
      }
    } catch (e) { /* best-effort */ }
  };

  // Start voice session — NATIVE ElevenLabs ConvAI pipeline.
  // Same audio stack as the prod orb widget: EL owns STT, LLM, TTS,
  // VAD, barge-in, and interruption. Replaces the old LiveKit +
  // Deepgram path which was cutting transcripts mid-sentence.
  const startSession = async () => {
    if (connectionState !== 'disconnected') return;

    try {
      setConnectionState('connecting');
      setError(null);
      setConversationHistory([]);
      setCurrentTranscript('');

      // 1. Ask backend for EL ConvAI credentials (conversation_token for
      //    WebRTC, signed_url as WebSocket fallback).
      const response = await api.post(`/api/agents/${agentId}/test-orb/session`);
      const {
        session_id,
        conversation_token,
        signed_url,
        dynamic_variables,
      } = response.data || {};
      if (!conversation_token && !signed_url) {
        throw new Error('No voice credentials returned');
      }

      sessionIdRef.current = session_id || null;
      providerConversationRef.current = null;
      sessionAgentIdRef.current = agentId;
      sessionStartTsRef.current = Date.now();
      sessionEndedRef.current = false;
      agentSpokeRef.current = false;

      // 2. Load the EL SDK (IIFE bundle — same one the prod orb widget
      //    uses; ESM build has a fetch-body-double-read bug in some
      //    proxy setups).
      const ELC = await loadElevenLabsSdk();
      if (!ELC || !ELC.Conversation) {
        throw new Error('ElevenLabs SDK failed to load');
      }

      // 3. Request mic permission up-front (required for both WebRTC + WS)
      //    and prime mobile audio (iOS Safari gates AudioContext + Audio
      //    element playback behind the same user-gesture tick).
      await navigator.mediaDevices.getUserMedia({ audio: true });
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          if (ctx.state === 'suspended') await ctx.resume();
          const src = ctx.createBufferSource();
          src.buffer = ctx.createBuffer(1, 1, 22050);
          src.connect(ctx.destination);
          src.start(0);
        }
      } catch (_) { /* best-effort */ }

      // 4. Start EL ConvAI session. WebRTC is preferred (conversationToken);
      //    WS signed URL is the fallback.
      const startOpts = {
        dynamicVariables: dynamic_variables || {},
        useWakeLock: false,
        onConnect: (event) => {
          providerConversationRef.current = event?.conversationId || event?.conversation_id || null;
          setConnectionState('connected');
          isActiveRef.current = true;
        },
        onDisconnect: () => {
          const startTs = sessionStartTsRef.current;
          if (!agentSpokeRef.current && startTs && (Date.now() - startTs) < 15000) {
            toast.error(
              "The voice session ended before the agent could speak — this usually means a configuration issue with the agent's greeting or voice. Please try again."
            );
          }
          reportSessionEnd();
          conversationRef.current = null;
          isActiveRef.current = false;
          setConnectionState('disconnected');
          setIsAgentSpeaking(false);
          setIsAgentThinking(false);
          setIsUserSpeaking(false);
          setAudioLevel(0);
        },
        onModeChange: (evt) => {
          const mode = evt && (evt.mode || evt);
          if (mode === 'speaking') {
            agentSpokeRef.current = true;
            setIsAgentSpeaking(true);
            setIsAgentThinking(false);
            setIsUserSpeaking(false);
          } else if (mode === 'listening') {
            setIsAgentSpeaking(false);
            setIsAgentThinking(false);
            setIsUserSpeaking(true);
          } else if (mode === 'thinking') {
            setIsAgentSpeaking(false);
            setIsAgentThinking(true);
            setIsUserSpeaking(false);
          }
        },
        onStatusChange: (evt) => {
          const s = evt && (evt.status || evt);
          if (s === 'disconnected') {
            setConnectionState('disconnected');
          }
        },
        onMessage: (msg) => {
          try {
            // EL ConvAI emits `{message, source}` where source is 'user'
            // or 'ai'. Push into the conversation panel so the UI still
            // shows the transcript.
            const source = msg?.source;
            const text = msg?.message;
            if (!text) return;
            if (source === 'user') {
              setCurrentTranscript('');
              setConversationHistory((prev) => [...prev, {
                role: 'user',
                content: text,
                timestamp: new Date().toISOString(),
              }]);
            } else if (source === 'ai') {
              agentSpokeRef.current = true;
              setConversationHistory((prev) => [...prev, {
                role: 'assistant',
                content: text,
                timestamp: new Date().toISOString(),
              }]);
            }
          } catch (_) { /* ignore */ }
        },
        onError: (err) => {
          console.error('[TestOrb] EL conversation error:', err);
          toast.error(err?.message || 'Voice error — try again');
        },
      };
      // Prefer the WebSocket signed URL over the WebRTC conversation
      // token. WebRTC delegates transport to LiveKit, and livekit-client
      // 2.x has a known "body stream already read" bug when the
      // signaling handshake fails through certain proxies. WebSocket
      // connects straight to EL's own `/v1/convai/conversation` endpoint
      // — same audio quality, but bypasses LiveKit entirely so the test
      // orb works in every browser + network combo.
      if (signed_url) {
        startOpts.signedUrl = signed_url;
        startOpts.connectionType = 'websocket';
      } else if (conversation_token) {
        startOpts.conversationToken = conversation_token;
      } else {
        throw new Error('No EL session credentials returned');
      }

      const conv = await ELC.Conversation.startSession(startOpts);
      conversationRef.current = conv;
      providerConversationRef.current = conv.getId?.() || providerConversationRef.current;
      // onConnect flips connectionState to 'connected' automatically; set
      // here as a safety net for SDK versions that don't fire onConnect
      // synchronously.
      setConnectionState('connected');
      isActiveRef.current = true;
      toast.success('Voice session started');
    } catch (err) {
      console.error('Failed to start voice session:', err);

      // 402 → balance exhausted. Show the top-up modal and bail cleanly.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402) {
        const info = (detail && typeof detail === 'object') ? detail : {};
        let rebilling = false;
        try {
          if (info.sub_account_id) {
            const r = await api.get(`/api/sub-accounts/${info.sub_account_id}`);
            rebilling = !!r?.data?.rebilling_enabled;
          }
        } catch (_) { /* best effort */ }
        setBalanceGate({
          workspaceId: info.workspace_id || '',
          subAccountId: info.sub_account_id || '',
          balance: typeof info.balance === 'number' ? info.balance : 0,
          rebilling,
        });
        setConnectionState('disconnected');
        return;
      }

      const message = typeof detail === 'string' ? detail : (err.message || 'Failed to connect');
      setError(message);
      if (conversationRef.current) {
        try { await conversationRef.current.endSession(); } catch (_) {}
        conversationRef.current = null;
      }
      reportSessionEnd();
      setConnectionState('disconnected');
      toast.error(message);
    }
  };

  const endSession = useCallback(async () => {
    isActiveRef.current = false;
    if (conversationRef.current) {
      try { await conversationRef.current.endSession(); } catch (_) {}
      conversationRef.current = null;
    }
    // onDisconnect above already calls reportSessionEnd; this is a safety
    // net for error paths where the SDK never fires onDisconnect. The
    // backend end endpoint is idempotent.
    reportSessionEnd();
    setConnectionState('disconnected');
    setIsAgentSpeaking(false);
    setIsAgentThinking(false);
    setIsUserSpeaking(false);
    setAudioLevel(0);
    setActiveFunctionCall(null);
    setCurrentTranscript('');
    if (audioLevelIntervalRef.current) {
      clearInterval(audioLevelIntervalRef.current);
      audioLevelIntervalRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMute = async () => {
    const conv = conversationRef.current;
    if (!conv) return;
    const newMuted = !isMuted;
    try {
      if (typeof conv.setMicMuted === 'function') {
        await conv.setMicMuted(newMuted);
      } else if (typeof conv.setMuted === 'function') {
        await conv.setMuted(newMuted);
      }
      setIsMuted(newMuted);
    } catch (e) {
      console.warn('Mute toggle failed:', e);
    }
  };

  const isConnected = connectionState === 'connected';
  const hasMessages = conversationHistory.length > 0;

  const clearConversation = () => {
    setConversationHistory([]);
    setCurrentTranscript('');
  };

  return (
    <div className="flex flex-col h-full overflow-hidden relative" style={isDarkMode ? { background: '#050507' } : { background: '#f8fafc' }}>

      {/* Balance-exhausted top-up modal — overlays the orb so the user
          can't accidentally retry into another 402. Redirect target is
          rebilling-aware: Agency→top up workspace, sub-account rebilling
          on→top up sub-account. */}
      <AnimatePresence>
        {balanceGate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex items-center justify-center"
            style={{ background: 'rgba(2,2,6,0.72)', backdropFilter: 'blur(8px)' }}
            data-testid="balance-exhausted-modal"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              className={`relative max-w-sm w-[88%] rounded-2xl p-6 border shadow-2xl ${isDarkMode ? 'bg-[#0c0d12] border-white/[0.08]' : 'bg-white border-gray-200'}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-red-500/15' : 'bg-red-50'}`}>
                  <AlertCircle className="text-red-500" size={22} />
                </div>
                <div>
                  <p className={`text-[10px] uppercase tracking-wider font-semibold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Balance exhausted</p>
                  <p className={`text-base font-semibold ${textPrimary}`}>Top up to test this agent</p>
                </div>
              </div>
              <p className={`text-sm leading-relaxed ${textSecondary} mb-5`}>
                Your workspace balance has reached $0. Calls, messages, chat & orb widgets, and test sessions are all paused until you add funds.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const { workspaceId, subAccountId, rebilling } = balanceGate;
                    // Rebilling ON → sub-account pays for itself. Rebilling OFF
                    // → agency pays (workspace billing page).
                    let target;
                    if (rebilling && subAccountId) {
                      target = `/workspace/${workspaceId}/sub-account/${subAccountId}/billing`;
                    } else if (workspaceId) {
                      target = `/workspace/${workspaceId}/billing`;
                    } else {
                      target = '/billing';
                    }
                    window.location.href = target;
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-400 hover:to-orange-400 transition"
                  data-testid="balance-gate-topup-btn"
                >
                  Go to Billing
                </button>
                <button
                  onClick={() => setBalanceGate(null)}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium transition ${isDarkMode ? 'bg-white/[0.06] text-gray-300 hover:bg-white/[0.1]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  data-testid="balance-gate-dismiss-btn"
                >
                  Later
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ambient background glow — subtle radial that complements the orb */}
      {isDarkMode && (
        <div 
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            background: isConnected 
              ? isAgentSpeaking 
                ? 'radial-gradient(ellipse at 50% 40%, rgba(16,185,129,0.06) 0%, rgba(6,182,212,0.03) 30%, transparent 65%)'
                : isAgentThinking
                ? 'radial-gradient(ellipse at 50% 40%, rgba(251,191,36,0.06) 0%, rgba(239,68,68,0.03) 30%, transparent 65%)'
                : 'radial-gradient(ellipse at 50% 40%, rgba(34,211,238,0.05) 0%, rgba(99,102,241,0.03) 30%, transparent 65%)'
              : 'radial-gradient(ellipse at 50% 40%, rgba(251,146,60,0.04) 0%, rgba(168,85,247,0.025) 30%, transparent 60%)',
            transition: 'background 1.5s ease'
          }}
        />
      )}

      {/* Top bar — clear button */}
      {hasMessages && !isConnected && (
        <div className="shrink-0 px-3 pt-2 z-20 flex">
          <button 
            onClick={clearConversation}
            className={`text-[11px] px-2.5 py-1 rounded-md font-medium transition-all ${isDarkMode ? 'text-gray-500 hover:text-gray-300 hover:bg-white/5' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
            data-testid="clear-conversation"
          >
            Clear
          </button>
        </div>
      )}

      {/* Orb area — centered vertically when no messages */}
      <div className={`flex flex-col items-center justify-center transition-all duration-500 ${hasMessages ? 'shrink-0 py-4' : 'flex-1 min-h-0'}`} style={hasMessages ? { minHeight: 190 } : undefined}>
        <div className="relative z-10">
          <VoiceOrb
            isActive={isConnected}
            isListening={isUserSpeaking || (isConnected && !isAgentSpeaking && !isAgentThinking)}
            isSpeaking={isAgentSpeaking}
            isThinking={isAgentThinking}
            audioLevel={audioLevel}
            functionCall={activeFunctionCall?.name}
            size={hasMessages ? 160 : 240}
            /* Voxinity brand colors — hard-coded so edits to a user's widget
               orb colors (Widgets page) never bleed into the agent-config
               test orb. Violet body, cyan icon — matches the Voxinity
               cyan→blue→violet product gradient. */
            primary="#8b5cf6"
            accent="#67e8f9"
          />
        </div>

        {/* Start button — directly below orb, both centered */}
        {!isConnected && !hasMessages && (
          <div className="text-center mt-6 z-10">
            <p className={`text-xs ${textSecondary} mb-3`}>Click to start a conversation</p>
            <motion.button
              onClick={startSession}
              className="flex items-center gap-2 px-6 py-2.5 rounded-full font-medium text-sm text-white mx-auto"
              style={{
                background: 'linear-gradient(135deg, #f97316 0%, #ef4444 40%, #a855f7 80%, #8b5cf6 100%)',
                boxShadow: '0 0 20px rgba(249,115,22,0.25), 0 0 40px rgba(168,85,247,0.1)'
              }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              data-testid="start-voice-session"
            >
              <Phone size={15} />
              Start Conversation
            </motion.button>
          </div>
        )}
      </div>

      {/* Reconnect button when disconnected but has transcript */}
      {!isConnected && hasMessages && (
        <div className="shrink-0 text-center pb-2 z-10">
          <motion.button
            onClick={startSession}
            className="flex items-center gap-2 px-5 py-2 rounded-full font-medium text-xs text-white mx-auto"
            style={{
              background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 50%, #8b5cf6 100%)',
              boxShadow: '0 0 15px rgba(6,182,212,0.2)'
            }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            data-testid="restart-voice-session"
          >
            <Phone size={13} />
            New Conversation
          </motion.button>
        </div>
      )}

      {/* Chat bubbles — show whenever there are messages (even after disconnect) */}
      {hasMessages && (
        <div
          className="flex-1 min-h-0 overflow-y-auto px-4 pb-2 z-10"
          style={{ scrollbarWidth: 'thin', scrollbarColor: isDarkMode ? 'rgba(6,182,212,0.2) transparent' : '#d1d5db transparent' }}
        >
            <div className="space-y-3 py-2">
              {conversationHistory.map((msg, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 12, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.25 }}
                >
                  {/* Function Call */}
                  {msg.role === 'function' && (
                    <div className="flex justify-center">
                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                        msg.error ? 'bg-red-500/10 text-red-300 border border-red-500/20' 
                        : msg.result ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20' 
                        : 'bg-violet-500/10 text-violet-300 border border-violet-500/20'
                      }`}>
                        {msg.result ? <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> 
                        : msg.error ? <div className="w-1.5 h-1.5 rounded-full bg-red-400" /> 
                        : <Loader2 size={10} className="animate-spin" />}
                        {msg.functionName}
                        {msg.result && <span className="text-[10px] font-bold uppercase opacity-70">OK</span>}
                        {msg.error && <span className="text-[10px] font-bold uppercase opacity-70">FAIL</span>}
                      </div>
                    </div>
                  )}
                  
                  {/* User bubble — right aligned */}
                  {msg.role === 'user' && (
                    <div className="flex justify-end">
                      <div 
                        className="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-br-md"
                        style={{
                          background: isDarkMode 
                            ? 'linear-gradient(135deg, rgba(6,182,212,0.2) 0%, rgba(59,130,246,0.15) 100%)'
                            : 'linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%)',
                          border: isDarkMode ? '1px solid rgba(6,182,212,0.2)' : '1px solid #bae6fd',
                          backdropFilter: 'blur(12px)',
                        }}
                      >
                        <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-cyan-50' : 'text-gray-800'}`}>
                          {msg.content}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Assistant bubble — left aligned */}
                  {msg.role === 'assistant' && (
                    <div className="flex justify-start">
                      <div 
                        className="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-bl-md"
                        style={{
                          background: isDarkMode 
                            ? 'rgba(255,255,255,0.05)'
                            : '#f1f5f9',
                          border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
                          backdropFilter: 'blur(12px)',
                        }}
                      >
                        <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                          {msg.content}
                        </p>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
              
              {/* Active function call */}
              {activeFunctionCall && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-center">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20">
                    <Loader2 size={10} className="text-violet-400 animate-spin" />
                    <span className="text-violet-300 text-xs font-medium">{activeFunctionCall.name}</span>
                  </div>
                </motion.div>
              )}
              
              {/* Live transcript */}
              {currentTranscript && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-end">
                  <div className="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-br-md" style={{
                    background: isDarkMode ? 'rgba(6,182,212,0.08)' : '#f0f9ff',
                    border: isDarkMode ? '1px dashed rgba(6,182,212,0.2)' : '1px dashed #bae6fd',
                  }}>
                    <p className={`text-sm italic ${isDarkMode ? 'text-cyan-300/60' : 'text-gray-500'}`}>
                      {currentTranscript}
                      <span className="inline-block w-0.5 h-4 ml-1 bg-cyan-400 animate-pulse" />
                    </p>
                  </div>
                </motion.div>
              )}
              
              {/* Thinking indicator */}
              {isAgentThinking && !currentTranscript && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                  <div className="px-4 py-2.5 rounded-2xl rounded-bl-md" style={{
                    background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                    border: isDarkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
                  }}>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-amber-400/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 rounded-full bg-amber-400/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 rounded-full bg-amber-400/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}

      {/* Error display */}
      <AnimatePresence>
        {error && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-4 py-2 bg-red-500/10 border-t border-red-500/30 shrink-0"
            data-testid="test-agent-voice-error"
          >
            <div className="flex items-center justify-center gap-2 text-red-400 text-sm">
              <AlertCircle size={14} />
              {error}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Controls bar */}
      {connectionState !== 'disconnected' && (
        <div className={`shrink-0 p-3 z-10 ${isDarkMode ? 'border-t border-white/5' : 'border-t border-gray-200'}`}>
          <div className="flex items-center justify-center gap-3">
            {connectionState === 'connecting' ? (
              <button disabled className={`flex items-center gap-2 px-6 py-3 rounded-full ${isDarkMode ? 'bg-gray-700/50 text-gray-400' : 'bg-gray-200 text-gray-500'} text-sm font-medium cursor-not-allowed`}>
                <Loader2 size={16} className="animate-spin" />
                Connecting...
              </button>
            ) : (
              <>
                <motion.button
                  onClick={toggleMute}
                  className={`p-3 rounded-full transition-all ${
                    isMuted
                      ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                      : isDarkMode ? 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10' : 'bg-gray-100 text-gray-500 border border-gray-300 hover:bg-gray-200'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid="mute-toggle"
                >
                  {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
                </motion.button>
                <motion.button
                  onClick={endSession}
                  className="flex items-center gap-2 px-5 py-3 rounded-full bg-red-500/90 text-white text-sm font-medium"
                  style={{ boxShadow: '0 0 20px rgba(239, 68, 68, 0.3)' }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="end-voice-session"
                >
                  <PhoneOff size={16} />
                  End
                </motion.button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AdvancedVoicePanel = (props) => (
  <VoicePanelErrorBoundary>
    <AdvancedVoicePanelInner {...props} />
  </VoicePanelErrorBoundary>
);

export default AdvancedVoicePanel;
